import { useEffect, useState } from "react";
import { Activity, Cpu, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { trackPageView } from "../lib/analytics";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import DrillLink from "../components/DrillLink";

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => { trackPageView("/commandlinc/analytics"); }, []);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: h }, { data: m }] = await Promise.all([
          api.get("/analytics/health"),
          api.get("/analytics/metrics"),
        ]);
        setHealth(h);
        setMetrics(m.metrics || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Unable to load analytics");
      }
    })();
  }, []);

  const gated = user?.role !== "director" && user?.role !== "admin";
  if (gated) {
    return (
      <>
        <TopBar title="Analytics" subtitle="Director & Admin only." />
        <div className="panel"><div className="panel-body"><EmptyState title="Insufficient permissions." /></div></div>
      </>
    );
  }

  return (
    <>
      <TopBar
        title="Analytics"
        subtitle="Event health & metrics catalog. Full dashboards land next phase."
      />

      {err ? (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-body"><span className="pill red">Error</span> {String(err)}</div>
        </div>
      ) : null}

      <div className="kpi-grid" data-testid="analytics-health-kpis">
        <div className="kpi cyan">
          <div className="label">Analytics enabled</div>
          <div className="value" data-testid="analytics-enabled">{String(health?.enabled ?? "…")}</div>
          <div className="trend">Server-side flag ANALYTICS_ENABLED</div>
        </div>
        <div className="kpi blue">
          <div className="label">Analytics events (total)</div>
          <div className="value" data-testid="analytics-total">{health?.total_events ?? "…"}</div>
          <div className="trend">All-time, UI/session/algorithm-internal</div>
        </div>
        <div className="kpi purple">
          <div className="label">Last 24h</div>
          <div className="value">{health?.events_last_24h ?? "…"}</div>
          <div className="trend">Rolling window</div>
        </div>
        <div className="kpi green">
          <div className="label">Ledger events (authoritative)</div>
          <div className="value">{health?.ledger_total_events ?? "…"}</div>
          <div className="trend">Domain events remain in the ledger</div>
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: 14 }}>
        <div className="panel" data-testid="analytics-top-events">
          <div className="panel-head">
            <div><h2><Activity size={14} /> Top events</h2><p>Ranked by count. Each row drills into the unified query view.</p></div>
          </div>
          <div className="panel-body">
            {(health?.top_events || []).length === 0 ? (
              <EmptyState title="No analytics events yet — click around to generate telemetry." />
            ) : (
              health.top_events.map((r) => (
                <DrillLink
                  key={r.event}
                  to={`/commandlinc/ledger?event=${encodeURIComponent(r.event)}`}
                  event="analytics.top_event_row"
                  properties={{ event: r.event, count: r.count }}
                  className="wide-row row-button"
                  testId={`analytics-top-event-${r.event}`}
                  style={{ width: "100%", display: "flex" }}
                >
                  <div>
                    <b className="mono" style={{ fontSize: 12 }}>{r.event}</b>
                    <p className="hint" style={{ fontSize: 11 }}>ledger + analytics_events combined</p>
                  </div>
                  <span className="pill dim mono">{r.count}</span>
                </DrillLink>
              ))
            )}
          </div>
        </div>

        <div className="panel" data-testid="analytics-metric-catalog">
          <div className="panel-head">
            <div><h2><TrendingUp size={14} /> Metric catalog</h2><p>Named metrics — foundation for next phase dashboards.</p></div>
          </div>
          <div className="panel-body">
            {metrics.length === 0 ? <EmptyState title="No metrics registered." /> :
              metrics.map((name) => (
                <div className="wide-row" key={name}>
                  <div>
                    <b className="mono" style={{ fontSize: 12 }}>{name}</b>
                    <p className="hint" style={{ fontSize: 11 }}>
                      <code style={{ fontSize: 10 }}>GET /api/analytics/metrics/{name}</code>
                    </p>
                  </div>
                  <span className="pill dim">
                    <Cpu size={10} style={{ marginRight: 4 }} /> foundation
                  </span>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 14 }}>
        <div className="panel-body">
          <p className="hint" style={{ fontSize: 12 }}>
            <b>Full analytics dashboard next phase.</b> Domain events (missions, approvals,
            cost, focus items, calendar events) continue to be written to the hash-chained
            Activity Ledger. The <code>analytics_events</code> collection captures only
            UI/session/algorithm-internal telemetry.
          </p>
        </div>
      </div>
    </>
  );
}
